import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, OutlinedInput,
    TextField, CircularProgress, Typography, Grid
} from '@material-ui/core';
import Close from '@material-ui/icons/CloseOutlined';
import Search from '@material-ui/icons/SearchOutlined';
import useFetch from "../../../../hooks/useFetch.js";
import Autocomplete from '@material-ui/lab/Autocomplete';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import { URL_ENDERECO_ESTADOS, URL_ENDERECO_CIDADES, URL_ENDERECO_CEPS } from '../../../../constants';
import ContainedButton from '../../../shared/buttons/ContainedButton';
import TextButton from '../../../shared/buttons/TextButton';
import Message from '../../../shared/message/Message.js';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        paddingTop: theme.spacing(2)
    },
    /* O botão desce até a linha do campo, já que o rótulo do LabelValue fica em cima. */
    colunaBotao: {
        display: 'flex',
        alignItems: 'flex-end'
    },
    botaoPesquisar: {
        height: 56
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2)
    },
    resultados: {
        marginTop: theme.spacing(3)
    },
    tituloResultados: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(1.5)
    }
}));

export default function DialogBuscarEndereco(props) {
    const { open, onClose, onSelect } = props;
    const classes = useStyles();
    const [fetch] = useFetch({ returnInnerLoadingState: true });

    const [estados, setEstados] = useState([]);
    const [selectedEstado, setSelectedEstado] = useState('');
    const [cidades, setCidades] = useState([]);
    const [selectedCidade, setSelectedCidade] = useState('');
    const [cidadeQuery, setCidadeQuery] = useState('');
    const [logradouro, setLogradouro] = useState('');
    const [ceps, setCeps] = useState([]);
    const [selectedCep, setSelectedCep] = useState(null);

    const [loadingEstados, setLoadingEstados] = useState(false);
    const [loadingCidades, setLoadingCidades] = useState(false);
    const [loadingCeps, setLoadingCeps] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => {
        if (open) {
            loadEstados();
        } else {
            resetState();
        }
    }, [open]);

    const resetState = () => {
        setSelectedEstado('');
        setCidades([]);
        setSelectedCidade('');
        setCidadeQuery('');
        setLogradouro('');
        setCeps([]);
        setSelectedCep(null);
        setErrorMsg('');
    };

    const loadEstados = async () => {
        setLoadingEstados(true);
        setErrorMsg('');
        fetch(
            URL_ENDERECO_ESTADOS,
            null,
            (data) => {
                setEstados(data || []);
                setLoadingEstados(false);
            },
            (error) => {
                setErrorMsg("Não foi possível carregar os estados.");
                setLoadingEstados(false);
            }
        );
    };

    const loadCidades = async (uf, nome) => {
        setLoadingCidades(true);
        setErrorMsg('');
        fetch(
            URL_ENDERECO_CIDADES,
            { uf, nome },
            (data) => {
                setCidades(data || []);
                setLoadingCidades(false);
            },
            (error) => {
                setErrorMsg("Não foi possível carregar as cidades deste estado.");
                setLoadingCidades(false);
            }
        );
    };

    useEffect(() => {
        if (!selectedEstado) return;

        const delayDebounceFn = setTimeout(() => {
            loadCidades(selectedEstado, cidadeQuery);
        }, 300);

        return () => clearTimeout(delayDebounceFn);
    }, [cidadeQuery, selectedEstado]);

    const handleEstadoChange = async (e) => {
        const uf = e.target.value;
        setSelectedEstado(uf);
        setSelectedCidade('');
        setCidadeQuery('');
        setCidades([]);
        setCeps([]);
        setSelectedCep(null);
        setErrorMsg('');

        if (uf) {
            loadCidades(uf, '');
        }
    };

    const handleCidadeChange = (e) => {
        setSelectedCidade(e.target.value);
        setCeps([]);
        setSelectedCep(null);
        setErrorMsg('');
    };

    const handleSearch = async () => {
        if (!selectedEstado) {
            setErrorMsg("É necessário selecionar um estado.");
            return;
        }
        if (!selectedCidade) {
            setErrorMsg("É necessário selecionar uma cidade.");
            return;
        }

        setLoadingCeps(true);
        setErrorMsg('');
        setCeps([]);
        setSelectedCep(null);

        fetch(
            URL_ENDERECO_CEPS,
            { uf: selectedEstado, cidadeId: selectedCidade, logradouro },
            (data) => {
                const results = data || [];
                setCeps(results);
                if (results.length === 0) {
                    setErrorMsg("Nenhum endereço encontrado.");
                }
                setLoadingCeps(false);
            },
            (error) => {
                setErrorMsg("Erro ao realizar a busca. Tente novamente.");
                setLoadingCeps(false);
            }
        );
    };

    const handleSelect = () => {
        if (selectedCep) {
            onSelect(selectedCep);
            onClose();
        }
    };

    return (
        <Dialog scroll="body" open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Pesquisar Endereço</DialogTitle>
            <DialogContent className={classes.dialogContent}>
                {errorMsg && (
                    <Message 
                        type={errorMsg === "Nenhum endereço encontrado." ? "warning" : "error"} 
                        variant="standard" 
                        marginBottom
                    >
                        {errorMsg}
                    </Message>
                )}

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Estado *">
                        <FormControl variant="outlined" fullWidth disabled={loadingEstados}>
                            <Select
                                displayEmpty
                                value={selectedEstado}
                                onChange={handleEstadoChange}
                            >
                                <MenuItem value="">
                                    <em>Selecione...</em>
                                </MenuItem>
                                {estados.map(est => (
                                    <MenuItem key={est.uf} value={est.uf}>
                                        {est.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        </LabelValue>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <LabelValue label="Cidade *">
                        <Autocomplete
                            id="autocomplete-cidade"
                            options={cidades}
                            getOptionLabel={(option) => option.nome || ''}
                            value={cidades.find(c => c.id === selectedCidade) || null}
                            onChange={(event, newValue) => {
                                setSelectedCidade(newValue ? newValue.id : '');
                                setCeps([]);
                                setSelectedCep(null);
                                setErrorMsg('');
                            }}
                            inputValue={cidadeQuery}
                            onInputChange={(event, newInputValue) => {
                                setCidadeQuery(newInputValue);
                            }}
                            disabled={!selectedEstado}
                            loading={loadingCidades}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="outlined"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {loadingCidades ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        </LabelValue>
                    </Grid>

                    <Grid item xs={12} sm={9}>
                        <LabelValue label="Logradouro">
                        <FormControl variant="outlined" fullWidth>
                            <OutlinedInput
                                placeholder="Não utilize nº de casa/apto/lote/prédio ou abreviação."
                                value={logradouro}
                                onChange={(e) => setLogradouro(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                            />
                        </FormControl>
                        </LabelValue>
                    </Grid>
                    <Grid item xs={12} sm={3} className={classes.colunaBotao}>
                        <ContainedButton
                            onClick={handleSearch}
                            fullWidth
                            startIcon={loadingCeps ? <CircularProgress size={16} color="inherit" /> : <Search />}
                            disabled={loadingCeps || !selectedEstado || !selectedCidade}
                            className={classes.botaoPesquisar}
                        >
                            Pesquisar
                        </ContainedButton>
                    </Grid>
                </Grid>

                {loadingCeps && (
                    <div className={classes.loadingContainer}>
                        <CircularProgress size={32} />
                    </div>
                )}

                {ceps.length > 0 && (
                    <div className={classes.resultados}>
                        <Typography variant="subtitle2" className={classes.tituloResultados}>
                            Selecione o endereço:
                        </Typography>
                        <DataGrid
                            responsive="card"
                            rows={ceps}
                            comparatorKeys={['cep', 'logradouro']}
                            sort
                            select
                            selectOptions={{
                                mode: 'single',
                                selectedRows: selectedCep ? [selectedCep] : [],
                                onSelect: (cepObj) => setSelectedCep(cepObj)
                            }}
                            columns={[
                                {
                                    label: 'Endereço',
                                    field: 'label',
                                    valueGetter: (value, row) => value || row?.label || '-'
                                }
                            ]}
                        />
                    </div>
                )}
            </DialogContent>
            <DialogActions>
                <TextButton onClick={onClose} startIcon={<Close />}>
                    Fechar
                </TextButton>
                <ContainedButton
                    onClick={handleSelect}
                    disabled={!selectedCep}
                >
                    Selecionar
                </ContainedButton>
            </DialogActions>
        </Dialog>
    );
}
